/* ============================================
   STATE - État global de l'application
   ============================================ */

import { CONFIG } from "./config.js";

// Etat global de l'application
export const state = {
  // ── MQTT ──────────────────────────────────────────────
  // Double connexion decalee pour couvrir les coupures broker
  mqttClient: null, // Connexion A (primaire)
  mqttClientB: null, // Connexion B (decalee de STAGGER_DELAY_MS)

  // File d'attente des messages (pour le delai)
  messageQueue: [],
  delayMs: 0,
  globalDelayMs: 0, // Delai global (chrono seul, sans offset modal) — applique aux buts

  // Mode batch replay : supprime les MAJ DOM couteuses pendant les seeks
  batchReplay: false,

  // ── Match ─────────────────────────────────────────────
  currentMatch: null,
  matchId: null,
  matchScheduledTime: null, // Timestamp programme du match (en secondes)

  // Etat du match
  lastScore: null,
  lastStats: null,
  players: {},
  events: [],

  // Dernier message F5
  lastF5Timestamp: null,

  // Timestamp du dernier message historique (pour eviter doublons live/historique)
  lastHistoryTimestamp: 0,

  // Type de match (avec ou sans positions)
  matchHasPositions: null,

  // Temps additionnel (en minutes)
  addedTime: null,

  // Séance de tirs aux buts (TAB)
  shootout: {
    active: false,
    vcActive: false,
    finished: false,
    home: { score: 0, attempts: [] },
    away: { score: 0, attempts: [] },
  },

  // FootballStaticDetail (lineups, equipes, stade) recupere via HTTP
  staticDetail: null,

  // Mapping playerId(int32) -> { name, shortName, shirtNumber, position, team }
  playerMapping: null,

  // Derniers MLiveItems decodes par protobufjs
  lastMLiveItems: null,

  // ── Ballon & possession ───────────────────────────────
  currentBallPosition: {
    x: null,
    y: null,
    possession: null,
  },

  // Historique des positions (pour la trainee)
  positionHistory: [],

  // Possession actuelle
  currentPossession: null,

  // Dernier joueur detecte (extrait des messages F5)
  lastPlayerInfo: {
    playerId: null,
    playerName: null,
  },

  // Systeme de priorite des evenements (delai minimum d'affichage)
  eventDisplay: {
    currentEvent: null, // Type d'evenement actuel
    currentPriority: 0, // Priorite (0=FLUX, 1=MOYEN, 2=MAJEUR)
    minEndTime: 0, // Timestamp minimum de fin d'affichage
    queue: [], // File d'attente des événements bloqués (priorité >= 1)
    drainTimerId: null, // Timer pour le drainage automatique de la file
  },

  // Etat du flip 3D de l'indicateur (carte event ↔ joueur)
  indicatorFlip: {
    lastEventType: null, // Dernier type d'event affiché
    lastPossession: null, // Dernière possession ('home'/'away')
    lastPlayerName: null, // Dernier nom de joueur affiché
    lastPlayerId: null, // Dernier ID de joueur
    lastPosKey: null, // Clé de position sérialisée "posX|posY"
    posStableTimestamp: null, // Timestamp quand la position s'est stabilisée
    isFlipped: false, // true = face joueur visible
    flipTimerId: null, // ID du setTimeout de 3s
  },

  // ── Equipes ───────────────────────────────────────────
  teamLogos: {
    home: null,
    away: null,
  },

  teamIds: {
    home: null,
    away: null,
  },

  // Couleurs des equipes (fallback pour les maillots SVG)
  teamColors: {
    home: "#E7E7E7", // Blanc par defaut
    away: "#0F0F14", // Noir par defaut
  },

  // URLs des maillots resolues par thesportsJerseyResolver (envoyees par React)
  jerseyUrls: {
    home: null,
    away: null,
  },

  // ── Terrain & affichage ───────────────────────────────
  // Dimensions du terrain (utilisees pour calculer le scale)
  fieldLength: CONFIG.DEFAULT_FIELD_LENGTH,
  fieldWidth: CONFIG.DEFAULT_FIELD_WIDTH,
  fieldScale: 1, // Scale calcule automatiquement

  // Couleurs du terrain (vert classique par defaut)
  fieldColors: {
    dark: "#1a4f29",
    light: "#1e5a2f",
  },

  // Couleurs des curseurs de position
  cursorColors: {
    home: "#228be6", // Bleu par defaut
    away: "#fab005", // Jaune par defaut
  },

  // Flag: le parent (React iframe) contrôle les couleurs des curseurs
  // Quand true, fetchStaticDetail ne doit pas écraser --cursor-*-color
  parentControlsColors: false,

  // Inversion des positions (symetrie terrain)
  // Si true: x -> 1-x, y -> 1-y, home <-> away
  invertPositions: false,

  // Mode d'affichage du terrain ('normal' = CSS 2D, '3d' = image perspective)
  fieldMode: "normal",

  // True quand au moins un overlay de données est actif (grille, king zone, récup., tiers, MVP, formation)
  // → masque notifications, indicateurs et overlays de possession
  dataOverlayActive: false,

  // Configuration du mode 3D (personnalisable par theme)
  field3DConfig: {
    imageUrl: null,
    imageWidth: 1079,
    imageHeight: 800,
    playableArea: {
      offsetX: 78,
      offsetY: 235,
      width: 923,
      height: 251,
    },
    trapezoid: {
      topEdgeWidth: 721,
      bottomEdgeWidth: 923,
      topEdgeOffsetX: 179,
      bottomEdgeOffsetX: 78,
    },
    perspectiveAngle: 22,
  },

  // Configuration du mode 3D-stadium (stade complet avec terrain en perspective)
  stadiumConfig: {
    imageUrl: null,
    imageWidth: 1920,
    imageHeight: 936,
    fieldCorners: {
      topLeft: { x: 537, y: 418 },
      topRight: { x: 1383, y: 418 },
      bottomLeft: { x: 61, y: 767 },
      bottomRight: { x: 1859, y: 767 },
    },
    fieldBounds: {
      left: 61,
      top: 418,
      width: 1798,
      height: 349,
    },
    // Centre du terrain (ligne mediane) relatif au coin haut-gauche du bounding box
    fieldCenter: { x: 899, y: 112 },
    perspectiveAngle: 22,
  },

  // ── UI & i18n ─────────────────────────────────────────
  // Langue de l'interface (francais par defaut)
  language: "fr",

  // ── Annotations F5 ───────────────────────────────────
  f5Messages: [],

  // Filtres d'annotations F5 (types d'evenements a afficher)
  f5AnnotationFilters: {
    showPossession: false,
    showAttaque: false,
    showAttaqueDangereuse: false,
    showTouche: true,
    showCorner: true,
    showRemplacement: true,
    showCartonJaune: true,
    showJoueurBlesse: true,
    showCoupFranc: true,
    showFrappeCadee: true,
    showTirsHorsCadre: true,
    show6Metres: true,
    showHorsJeu: true,
    showPenalty: true,
    showBut: true,
    showNotificationBut: true,
    showCoupEnvoi: true,
    showBallonPerdu: true,
    showBallonRecupere: true,
    showBallonsPerdus: true,
    showCartonRouge: true,
    showFinMatch: true,
    showInconnu: false,
    showVAR: true,
    showMiTemps: true,
    showTempsAdditionnel: true,
    showAutre: true,
  },

  // Etat d'affichage de l'interface de filtrage
  showF5FilterInterface: false,

  // ── Commentary ──────────────────────────────────────
  // Trend data (from MQTT code 10106)
  trendData: null,
  // Odds data (from MQTT code 10103)
  oddsData: null,

  // ── Statistiques de session ───────────────────────────
  stats: {
    totalMessages: 0,
    positions: 0,
    events: 0,
    startTime: null,
  },
};

// Fonctions utilitaires pour l'état
export function resetMatchState() {
  state.lastStats = null;
  state.lastF5Timestamp = null;
  state.lastHistoryTimestamp = 0;
  state.matchHasPositions = null;
  state.lastScore = null;
  state.positionHistory = [];
  state.currentBallPosition = { x: null, y: null, possession: null };
  state.currentPossession = null;
  state.shootout = {
    active: false,
    vcActive: false,
    finished: false,
    home: { score: 0, attempts: [] },
    away: { score: 0, attempts: [] },
  };
  if (state.eventDisplay.drainTimerId) {
    clearTimeout(state.eventDisplay.drainTimerId);
  }
  state.eventDisplay = {
    currentEvent: null,
    currentPriority: 0,
    minEndTime: 0,
    queue: [],
    drainTimerId: null,
  };
  if (state.indicatorFlip.flipTimerId) {
    clearTimeout(state.indicatorFlip.flipTimerId);
  }
  state.indicatorFlip = {
    lastEventType: null,
    lastPossession: null,
    lastPlayerName: null,
    lastPlayerId: null,
    lastPosKey: null,
    posStableTimestamp: null,
    isFlipped: false,
    flipTimerId: null,
  };
}

export function resetSessionStats() {
  state.stats = {
    totalMessages: 0,
    positions: 0,
    events: 0,
    startTime: Date.now(),
  };
}

export function clearF5Messages() {
  state.f5Messages = [];
}
